import React from 'react';

// Enums
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

// Components
import ExternalLink from '@/views/components/ExternalLink';
import { FormattedMessageV2 } from '@/views/components/i18n';
import { PrimaryButton, SecondaryLink } from '@/views/components/buttons';

// Libraries
import classNames from 'classnames';

// Utils
import { formatMessage } from '@/utils/i18n';

export enum EmptyStateCardSize {
  Default = 'default',
  Small = 'small',
}

type ActionButtonProps = {
  icon?: string;
  label: string;
};

// Primary Action
type PrimaryButtonProps = ActionButtonProps & {
  onClick: () => void;
};

// Secondary Actions
type ExternalLinkProps = ActionButtonProps & {
  isExternalLink?: boolean;
  to: string;
};

type SecondaryLinkProps = ActionButtonProps & {
  isExternalLink?: boolean;
  onClick: () => void;
};

export type Props = {
  className?: string;
  imagePath?: string;
  imageAlt?: string;
  links?: (ExternalLinkProps | SecondaryLinkProps)[];
  messagePath?: string;
  primaryButton?: PrimaryButtonProps;
  titlePath?: string;
  i18nVariables?: {
    [key: string]: string;
  };
  size?: EmptyStateCardSize;
  customContentElement?: JSX.Element;
};

const EmptyStateCard = (props: Props) => {
  const {
    className,
    customContentElement,
    i18nVariables,
    imageAlt,
    imagePath,
    links,
    messagePath,
    primaryButton,
    titlePath,
    size = EmptyStateCardSize.Default,
  } = props;

  const renderImage = (): JSX.Element | null =>
    imagePath ? <img alt={imageAlt || 'empty state image'} className="empty-state-card-image" src={imagePath} /> : null;

  const renderTitle = (): JSX.Element | null =>
    titlePath ? <FormattedMessageV2 className="empty-state-card-title" tagName={ALLOWED_HTML_TAGS.P} toLocalize={titlePath} values={i18nVariables} /> : null;

  const renderMessage = (): JSX.Element | null =>
    messagePath ? <p className="empty-state-card-message">{formatMessage(messagePath, i18nVariables)}</p> : null;

  const renderPrimaryButton = (): JSX.Element | null => {
    if (!primaryButton) {
      return null;
    }

    return (
      <PrimaryButton className="primary-v2" data-testid="emptyStateCard-primary-btn" icon={primaryButton.icon} onClick={primaryButton.onClick}>
        {formatMessage(primaryButton.label)}
      </PrimaryButton>
    );
  };

  const renderLinks = () => {
    if (!links || links.length === 0) return null;

    return links.map((link, index) => {
      const linkType = link.isExternalLink ? 'external' : 'secondary';
      const linkTypeAndIndex = `${linkType}-link-${index}`;
      const linkData = `emptyStateCard-${linkTypeAndIndex}`;

      if (link.isExternalLink) {
        const extLink = link as ExternalLinkProps;
        return (
          <ExternalLink className="external-link-v2" data-testid={linkData} key={linkTypeAndIndex} to={extLink.to}>
            {extLink.icon && <i className={`icon-${extLink.icon}`} />} {formatMessage(extLink.label)}
          </ExternalLink>
        );
      }
      const secLink = link as SecondaryLinkProps;
      return (
        <SecondaryLink className="secondary-link-v2" data-testid={linkData} icon={secLink.icon} key={linkTypeAndIndex} onClick={secLink.onClick}>
          {formatMessage(secLink.label)}
        </SecondaryLink>
      );
    });
  };

  return (
    <div className="empty-state-card-container">
      <div className={classNames('empty-state-card', { small: size === EmptyStateCardSize.Small }, className)} data-testid="emptyStateCard">
        {renderImage()}
        {renderTitle()}
        {renderMessage()}
        {renderPrimaryButton()}
        {renderLinks()}
        {customContentElement}
      </div>
    </div>
  );
};

export default EmptyStateCard;
